'use client';

import React, { useEffect, useMemo } from 'react';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { createPortal } from 'react-dom';
import { EyeSlashIcon, MagnifyingGlassPlusIcon, SlidersIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import {
  formatProvenanceStatusLabel,
  getStatusColor,
  isStatus,
} from '@/shared/clientFunctions';
import { ProvenanceIcon } from '@/shared/provenance-icon';
import { META_FILE_REGEX, emailViewerS3KeyFromUrl } from '@/shared/constants';
import {
  fileInfoFromProvenanceBody,
  getCaseDisplayName,
  isContainerLikeSource,
  isUfdrRootSource,
  isZipExportRoot,
  pathForAssistantScope,
  toProvenanceLookupPath,
} from '@/shared/functions';
import type { Source } from '@/shared/types';
import { useMediaPlayerTransitionEnabled } from '@/hooks/useMediaPlayerTransition';
import EvidenceLockerTreeNew from './evidence-locker-tree-new';
import { EvidenceLockerPreviewOverlay } from './evidence-locker-preview-overlay';
import { EvidenceLockerMobileForensicOverlay } from './evidence-locker-mobile-forensic-overlay';
import { lockerPreviewStep } from './evidence-locker-preview-gate';
import { DismissibleBlurOverlay } from './dismissible-blur-overlay';
import type { EventFromChat } from './answer-item';
import { EvidenceLockerBrowseFilterChips, buildEvidenceLockerPreviewSource } from './evidence-locker-ui-shared-new';
import { useEvidenceLockerPanelNew } from './use-evidence-locker-panel-new';
import { useChatEvidenceLockerNew } from './chat-evidence-locker-context-new';
import {
  EvidenceLockerAppearanceContext,
  type EvidenceLockerAppearance,
} from './evidence-locker-appearance';
import './evidence-locker-inverse.css';
import { CsamDetectionPill } from './ui/media/csam-detection-pill';
import { ContentModerationAckModal } from './ui/content-moderation-ack-modal';
import { Button } from '@tranquility-ai/theme-components/button';
import {
  evidenceLockerHeaderActionButtonClassName,
  evidenceLockerToolbarIconButtonClassName,
} from './evidence-locker-button';
import { ProvenanceFileInfoBody, TreeNodeResponse } from '@/shared/cors-provenance-api';
import { useCsamAccess } from '@/lib/csam-access';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { useEmailArchivePreview } from '@/components/email-viewer/EmailArchivePreviewProvider';
import { mailboxPreviewTarget } from '@/components/email-viewer/mailbox-preview-target';

/** Keep panel interactions from reaching modal backdrops / document outside-click handlers. */
function stopPanelEventBubble(e: React.SyntheticEvent) {
  e.stopPropagation();
}

/**
 * Resolve the content-moderation type to display for a tree row.
 *
 * The tree node is the source of truth for what the user is browsing, so prefer
 * the moderation data embedded on the node itself (``fileInfo.file.contentModeration``).
 * This is what fixes deeply nested files: they are returned by ``GET /tree`` with
 * their flag intact but may never appear in the first page of the flat ``/files``
 * sidecar list that ``availableEvidence`` is built from. Fall back to the flat-list
 * lookup only when the node carries no file-level moderation data (e.g. subdocuments,
 * whose envelope does not include ``contentModeration``). A ``REVIEWED`` finding is
 * treated as cleared, matching how ``availableEvidence`` is derived.
 */
function resolveRowContentModerationType(
  node: TreeNodeResponse | undefined,
  fallback: string | null,
): string | null {
  const fi = node?.fileInfo;
  // Both top-level files and extracted subdocuments carry their own
  // content-moderation metadata; read whichever the node is.
  const cm =
    fi?.kind === 'file'
      ? fi.file.contentModeration
      : fi?.kind === 'subdocument'
        ? fi.subdocument.contentModeration
        : undefined;
  // Gate on the moderation payload, not on ``fileInfo`` presence: a node can
  // carry ``fileInfo`` while its ``contentModeration`` is null/absent (e.g. a
  // subdocument whose envelope omits it, or a file returned by ``/tree`` without
  // the embedded finding). Only when the node actually has a payload is it
  // authoritative — surface its type unless the finding is ``REVIEWED`` (cleared).
  if (cm) {
    if (cm.type && cm.findingStatus !== 'REVIEWED') return cm.type;
    return null;
  }
  // No payload on the node: fall back to the flat-list lookup so flagged files
  // that only surface in ``availableEvidence`` keep their CM tag, ack gate, and
  // obfuscated preview.
  return fallback;
}

export type EvidenceLockerPanelNewProps = {
  isActive?: boolean;
  /** Omit on surfaces with no timeline (HelioTrace); the create-event actions are then hidden. */
  addToTimeline?: (event: EventFromChat) => void;
  /** Called after the user clicks "Add N Selected". */
  onAppliedSelection?: () => void;
  onClose: () => void;
  /** Hide the panel X when a parent already provides Back / Cancel. */
  showCloseButton?: boolean;
  /** Dark filled-field chrome for ink surfaces such as Create Timeline. */
  appearance?: EvidenceLockerAppearance;
  /** Fires when the source preview dismiss overlay is shown or hidden. */
  onPreviewPopoverOpenChange?: (open: boolean) => void;
  showFileTypeFilters?: boolean;
  onToggleFileTypeFilters?: () => void;
  treeHeightClassName?: string;
};

export function EvidenceLockerPanelNew({
  isActive = true,
  addToTimeline,
  onAppliedSelection,
  onClose,
  showCloseButton = true,
  appearance = 'default',
  onPreviewPopoverOpenChange,
  showFileTypeFilters = false,
  onToggleFileTypeFilters,
  treeHeightClassName = 'min-h-0 flex-1',
}: EvidenceLockerPanelNewProps) {
  const t = useTimePilotT();
  const themeEmailArchive = useMediaPlayerTransitionEnabled('emailArchive');
  const emailArchivePreview = useEmailArchivePreview();
  const { browseRequest } = useChatEvidenceLockerNew();
  const panel = useEvidenceLockerPanelNew({ isActive, addToTimeline });
  const themeUfdrEnabled = useMediaPlayerTransitionEnabled('ufdr');
  // ``canSeeCsamTags`` is the tag-level gate: when off, no CM tag, ack gate, or
  // obfuscated preview is surfaced anywhere in the evidence locker.
  const { canSeeCsamTags } = useCsamAccess();
  const browseFiltersActive =
    !panel.allFileTypes && panel.activeFileTypeChipCount > 0;
  const inverse = appearance === 'inverse';
  const buttonBackground = inverse ? 'inverse' : 'default';
  const iconButtonColor = inverse ? 'light' : 'subdued';
  const headerActionColor = inverse ? 'light' : 'accent';
  const raisePreviewAboveModal = inverse || !!panel.ufdrPreviewContext;
  const previewZIndexClass = raisePreviewAboveModal ? 'z-[100002]' : 'z-[10053]';
  const previewBackdropZIndexClass = raisePreviewAboveModal ? 'z-[100001]' : 'z-[10052]';

  useEffect(() => {
    onPreviewPopoverOpenChange?.(panel.isSourceCardPopoverOpen);
  }, [onPreviewPopoverOpenChange, panel.isSourceCardPopoverOpen]);

  const closePreview = () => {
    panel.handleSetPreviewPopoverOpen(-1);
    panel.setPreviewPopoverUrl(null);
  };

  const activePreview = useMemo(() => {
    if (panel.previewPopoverOpen >= 0) {
      const item = panel.availableEvidence[panel.previewPopoverOpen];
      if (item?.url) {
        return {
          url: item.url,
          pageCount: panel.previewPageCount,
          contentModerationType: item.contentModerationType ?? null,
          previewUrl: item.previewUrl ?? null,
          label: item.url.split('/').pop() ?? item.url,
          citationSource: buildEvidenceLockerPreviewSource(
            item.url,
            item.fileInfo,
            item.pageCount ?? panel.previewPageCount,
          ),
        };
      }
    }
    if (panel.previewPopoverUrl) {
      return {
        url: panel.previewPopoverUrl,
        pageCount: panel.previewPageCount,
        contentModerationType: panel.previewPopoverCmType,
        previewUrl: panel.previewPopoverPreviewUrl,
        label: panel.previewPopoverUrl.split('/').pop() ?? panel.previewPopoverUrl,
        citationSource: buildEvidenceLockerPreviewSource(
          panel.previewPopoverUrl,
          panel.previewPopoverFileInfo,
          panel.previewPageCount,
        ),
      };
    }
    return null;
  }, [
    panel.previewPopoverOpen,
    panel.previewPopoverUrl,
    panel.previewPageCount,
    panel.previewPopoverCmType,
    panel.previewPopoverPreviewUrl,
    panel.previewPopoverFileInfo,
    panel.availableEvidence,
  ]);

  const renderSearchToolbar = (
    searchPlaceholder: string,
    searchAriaLabel: string,
  ) => (
    <div>
      <div className="flex min-w-0 w-full items-center gap-4 pl-4 pr-2 py-2">
      <input
        type="text"
        aria-label={searchAriaLabel}
        className={`box-border min-h-[54px] min-w-0 flex-1 bg-transparent text-[21px] leading-tight outline-none ${
          inverse
            ? 'text-[var(--color-text-inverse,#ffffff)] placeholder:text-[var(--color-text-placeholder,#9ab0c4)]'
            : 'text-black placeholder:text-black/50'
        }`}
        placeholder={searchPlaceholder}
        value={panel.filesInputValue || ''}
        onChange={(e) => {
          panel.setEvidenceInputValue(e.target.value);
          panel.setScopeInputValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
      />
      <div className="flex shrink-0 items-center gap-1">
        {panel.contentCategoriesEnabled && onToggleFileTypeFilters ? (
          <TpTooltip
            content={
              showFileTypeFilters
              ? t('chat.evidence.filters.hideFileTypes')
              : t('chat.evidence.filters.showFileTypesHelp')
            }
            placement="bottom"
            delayDuration={400}
            sideOffset={6}
          >
            <Button
              type="button"
              size="sm"
              style={showFileTypeFilters || browseFiltersActive ? 'solid' : 'text'}
              color={showFileTypeFilters || browseFiltersActive ? 'accent' : iconButtonColor}
              background={buttonBackground}
              aria-label={
                showFileTypeFilters
                  ? t('chat.evidence.filters.hideFileTypes')
                  : t('chat.evidence.filters.showFileTypes')
              }
              aria-pressed={showFileTypeFilters}
              className={`relative ${evidenceLockerToolbarIconButtonClassName}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFileTypeFilters();
              }}
            >
              <SlidersIcon size={30} weight="regular" />
              {!panel.allFileTypes && panel.activeFileTypeChipCount > 0 ? (
                <span
                  className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-black/60 px-1 text-[11px] font-medium leading-none text-white"
                  aria-hidden
                >
                  {panel.activeFileTypeChipCount}
                </span>
              ) : null}
            </Button>
          </TpTooltip>
        ) : null}
        {showCloseButton ? (
        <Button
          type="button"
          size="sm"
          style="text"
          color={iconButtonColor}
          background={buttonBackground}
          aria-label={t('chat.evidence.closeLocker')}
          className={evidenceLockerToolbarIconButtonClassName}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          <XIcon size={30} weight="regular" />
        </Button>
        ) : null}
      </div>
      </div>
      {panel.contentCategoriesEnabled && showFileTypeFilters ? (
        <div
          aria-hidden
          className={`border-b-[1px] border-solid ${
            inverse ? 'border-[var(--color-ink-075,#1a263e)]' : 'border-[#E5E5E5]'
          }`}
        />
      ) : null}
      {panel.contentCategoriesEnabled && showFileTypeFilters ? (
        <EvidenceLockerBrowseFilterChips
          allFileTypes={panel.allFileTypes}
          activeFileTypes={panel.activeFileTypes}
          onToggleAllFileTypes={panel.toggleAllFileTypes}
          onToggleFileType={panel.toggleFileType}
        />
      ) : null}
    </div>
  );
  const renderSectionDivider = () => (
    <div
      aria-hidden
      className={`border-b-[1px] border-solid ${
        inverse ? 'border-[var(--color-ink-075,#1a263e)]' : 'border-[#E5E5E5]'
      }`}
    />
  );

  const lockerHeaderActions = (
    <>
      {panel.hasAnyPick ? (
        <Button
          type="button"
          style="text"
          color={headerActionColor}
          background={buttonBackground}
          size="sm"
          aria-label={t('chat.evidence.clearAllSelections')}
          className={evidenceLockerHeaderActionButtonClassName}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            panel.handleClearPendingSelections();
          }}
        >
          {t('chat.evidence.clearAll')}
        </Button>
      ) : null}
      <Button
        type="button"
        style="text"
        color={headerActionColor}
        background={buttonBackground}
        size="sm"
        aria-label={t('chat.evidence.addAllVisible')}
        className={`${evidenceLockerHeaderActionButtonClassName} ${panel.isIncludeAllActive ? 'hidden' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          panel.handleSelectAll();
        }}
      >
        {t('chat.evidence.addAll')}
      </Button>
    </>
  );

  // True whenever the user has any criterion that the tree's own
  // ``/v1/tree/search`` query depends on. We must keep the tree mounted in
  // that case even if the panel-level ``/v1/files`` query (which feeds
  // ``panel.hasResults``) returns zero rows: the two endpoints have
  // different matching semantics, and unmounting the tree on the panel
  // query's empty result aborts the tree's in-flight ``/v1/tree/search``
  // (Firefox surfaces this as NS_BINDING_ABORTED) and hides results the
  // tree was about to render.
  const hasActiveSearchCriteria =
    !!(panel.filesSearchTerm && panel.filesSearchTerm.trim().length > 2) ||
    panel.activeFileTypeChipCount > 0 ||
    panel.evidenceLockerCategoryScopeActive;

  const renderResultsArea = (wrapperClassName: string) => (
    <>
      {/* No case tag means no scoped query was allowed to run, so there is
          nothing to browse — say so rather than render an empty tree. */}
      {panel.caseScopeMissing ? (
        <div className="flex h-full items-center justify-center px-6 text-center">
          <div className={`pt-3 text-sm ${inverse ? 'text-[var(--color-text-placeholder,#9ab0c4)]' : 'text-black/60'}`}>
            {t('chat.evidence.noIndexedSources')}
          </div>
        </div>
      ) : panel.hasResults || hasActiveSearchCriteria ? (
        <div className={`${wrapperClassName} min-h-0 min-w-0 w-full`}>
          <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
            {(panel.showEvidenceRowsWhenFoldersOn || panel.isFoldersOnlyMode) && (
              <div
                className={`${treeHeightClassName} flex min-h-0 min-w-0 w-full flex-col overflow-hidden ${
                  inverse ? 'bg-[var(--color-surface-inverse-selected,#344465)]' : 'bg-white'
                }`}
              >
                <EvidenceLockerTreeNew
                  ref={panel.evidenceTreeRef}
                  browseRequest={browseRequest}
                  mode={panel.isFoldersOnlyMode ? 'folders' : 'tree'}
                  searchQuery={panel.filesSearchTerm}
                  contentCategories={panel.evidenceFilesFilters.contentCategories}
                  caseTag={panel.caseTag}
                  caseScopeUnresolved={panel.caseScopeUnresolved}
                  scopeCaseFolderMatchId={
                    panel.caseId === '*' ||
                    panel.caseId === 'All Cases' ||
                    !panel.caseId?.trim()
                      ? null
                      : panel.caseId.trim()
                  }
                  autoBrowseSubfolderName={panel.adHocAutoBrowseSubfolder}
                  pickedPaths={panel.pickedEvidenceSet}
                  excludedPaths={panel.excludedEvidenceSet}
                  pickedFolderPaths={panel.pickedScopesSet}
                  excludedFolderPaths={panel.excludedScopesSet}
                  toUrl={toProvenanceLookupPath}
                  headerActions={lockerHeaderActions}
                  onTogglePick={(url) => panel.cycleFileScope(url)}
                  onTogglePickFolder={(url) => panel.cycleFolderScope(url)}
                  onClearSearch={() => {
                    panel.setEvidenceInputValue('');
                    panel.setScopeInputValue('');
                  }}
                  renderRowPreview={({ url, mediaType, pageCount, node }) => {
                    const fi = node.fileInfo;
                    const provenanceStatus =
                      fi?.kind === 'file'
                        ? fi.file.status
                        : fi?.kind === 'subdocument'
                          ? fi.subdocument.status
                          : undefined;
                    const provenanceStatusMessage =
                      fi?.kind === 'file'
                        ? fi.file.statusMessage
                        : fi?.kind === 'subdocument'
                          ? fi.subdocument.statusMessage
                          : undefined;
                    const provenanceStatusTooltip =
                      isStatus(provenanceStatus, 'SKIPPED') && provenanceStatusMessage?.trim()
                        ? `Status: ${provenanceStatus} - ${provenanceStatusMessage.trim()}`
                        : formatProvenanceStatusLabel(provenanceStatus, 'UNKNOWN');
                    const index = panel.availableEvidence.findIndex(
                      (e) => e.url === url,
                    );
                    const sourceObject = index >= 0 ? panel.availableEvidence[index] : null;
                    const cmType = canSeeCsamTags
                      ? resolveRowContentModerationType(
                          node,
                          sourceObject?.contentModerationType ?? null,
                        )
                      : null;
                    const fileName = url.split('/').pop() ?? url;
                    const cmPreviewUrl =
                      sourceObject?.previewUrl ??
                      (fi ? fileInfoFromProvenanceBody(fi as ProvenanceFileInfoBody)?.previewUrl : null) ??
                      null;
                    let useMetaViewer = false;
                    let useEmailViewer = false;
                    let s3Key = '';
                    if (mediaType === 'html' || /^(html|htm)$/i.test(mediaType)) {
                      s3Key = url.startsWith('s3://')
                        ? url.split('/').slice(3).join('/')
                        : url;
                      const lastPart = (s3Key.split('/').pop() || '').toLowerCase();
                      if (META_FILE_REGEX.test(lastPart)) {
                        useMetaViewer = true;
                      }
                    }
                    // Mbox / pst / ost should open the standalone Outlook-style
                    // ``/email-viewer`` regardless of how the row's mediaType was
                    // classified — the loader maps mbox to ``text``, but other
                    // tenants may surface these as something else entirely.
                    if (!useMetaViewer) {
                      const emailKey = emailViewerS3KeyFromUrl(url);
                      if (emailKey) {
                        useEmailViewer = true;
                        s3Key = emailKey;
                      }
                    }
                    // Container types (zip, ufdr, …) only render meaningfully
                    // via their extracted children; the preview control would
                    // open a SourceCard with nothing to show. Mailbox
                    // containers are the exception — they open ``/email-viewer``.
                    const fileSummary = fi
                      ? fileInfoFromProvenanceBody(fi as ProvenanceFileInfoBody)
                      : null;
                    const isContainer = isContainerLikeSource(
                      { sourceFilePath: url } as Source,
                      fileSummary,
                    );
                    const isUfdrRoot =
                      themeUfdrEnabled &&
                      (isUfdrRootSource(
                        { sourceFilePath: url } as Source,
                        fileSummary,
                      ) ||
                        isZipExportRoot(
                          { sourceFilePath: url } as Source,
                          fileSummary,
                        ));
                    const showPreviewButton = !isContainer || useEmailViewer || isUfdrRoot;
                    return (
                      <div className="flex flex-shrink-0 items-center gap-1">
                        <TpTooltip
                          placement="left"
                          delayDuration={100}
                          sideOffset={4}
                          content={provenanceStatusTooltip}
                        >
                          <Button
                            type="button"
                            iconOnly
                            size="sm"
                            style="text"
                            color={iconButtonColor}
                            background={buttonBackground}
                            aria-label={provenanceStatusTooltip}
                            className="h-7 w-7 shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <ProvenanceIcon
                              status={provenanceStatus}
                              size={16}
                              className={getStatusColor(provenanceStatus)}
                              aria-hidden
                            />
                          </Button>
                        </TpTooltip>
                        {showPreviewButton && (
                          <Button
                            type="button"
                            iconOnly
                            size="sm"
                            style="text"
                            color={iconButtonColor}
                            background={buttonBackground}
                            className="h-7 w-7 shrink-0"
                            aria-label={t('chat.evidence.previewSource')}
                            aria-haspopup="dialog"
                            aria-expanded={
                              activePreview?.url === url && panel.isSourceCardPopoverOpen
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const ufdrPreview = {
                                url,
                                path: toProvenanceLookupPath(fileSummary?.searchablePath || url),
                                fileStatusId:
                                  node.fileStatusId ??
                                  (node.nodeType === 'file' ? node.id : null) ??
                                  fileSummary?.fileStatusId ??
                                  null,
                                label: fileName,
                                fileInfo: fileSummary,
                              };
                              const previewStep = lockerPreviewStep(isUfdrRoot, cmType);
                              if (previewStep.step === 'acknowledge' && cmType) {
                                panel.setContentModerationAck({
                                  type: cmType,
                                  index,
                                  url,
                                  pageCount: pageCount ?? null,
                                  previewUrl: cmPreviewUrl,
                                  fileInfo: fileSummary,
                                  ufdr: previewStep.opensUfdrAfterAck ? ufdrPreview : null,
                                });
                              } else if (previewStep.step === 'ufdr') {
                                panel.handleOpenUfdrPreview(ufdrPreview);
                              } else if (url && useMetaViewer) {
                                const caseParam =
                                  panel.caseId && panel.caseId.trim() !== ''
                                    ? `&caseId=${encodeURIComponent(panel.caseId)}`
                                    : '';
                                window.open(
                                  `/meta-viewer?s3Key=${encodeURIComponent(s3Key)}${caseParam}`,
                                  '_blank',
                                );
                              } else if (url && useEmailViewer) {
                                const caseParam =
                                  panel.caseId && panel.caseId.trim() !== ''
                                    ? `&caseId=${encodeURIComponent(panel.caseId)}`
                                    : '';
                                if (
                                  mailboxPreviewTarget(themeEmailArchive) === 'shell' &&
                                  emailArchivePreview
                                ) {
                                  emailArchivePreview.open({ s3Key });
                                } else {
                                  window.open(
                                    `/email-viewer?s3Key=${encodeURIComponent(s3Key)}${caseParam}`,
                                    '_blank',
                                  );
                                }
                              } else if (index >= 0) {
                                panel.handleSetPreviewPopoverOpen(
                                  index,
                                  pageCount ?? undefined,
                                );
                              } else if (url) {
                                panel.handleSetPreviewPopoverOpenForUrl(
                                  url,
                                  pageCount ?? undefined,
                                  undefined,
                                  undefined,
                                  fileSummary,
                                );
                              }
                            }}
                          >
                            {cmType ? (
                              <EyeSlashIcon
                                weight={panel.hoveredIconKey === url ? 'fill' : 'regular'}
                                className="text-red-400 transition-all duration-200"
                                onMouseEnter={() => panel.setHoveredIconKey(url)}
                                onMouseLeave={() => panel.setHoveredIconKey(null)}
                              />
                            ) : (
                              <MagnifyingGlassPlusIcon
                                weight={
                                  panel.hoveredIconKey === url ? 'fill' : 'bold'
                                }
                                onMouseEnter={() => panel.setHoveredIconKey(url)}
                                onMouseLeave={() => panel.setHoveredIconKey(null)}
                              />
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  }}
                  renderRowNameSuffix={({ url, node }) => {
                    if (!canSeeCsamTags) return null;
                    const idx = panel.availableEvidence.findIndex((e) => e.url === url);
                    const src = idx >= 0 ? panel.availableEvidence[idx] : null;
                    const cmType = resolveRowContentModerationType(
                      node,
                      src?.contentModerationType ?? null,
                    );
                    if (!cmType) return null;
                    return (
                      <div className="flex flex-row gap-1 shrink-0">
                        <CsamDetectionPill type={cmType} />
                      </div>
                    );
                  }}
                  renderRowFileActions={({ node }) => {
                    if (!panel.isAdHocManualTreeFile(node)) return null;
                    const fileName = node.name.trim();
                    if (!fileName) return null;
                    const isDeleting = panel.purgingManualFileName === fileName;
                    return (
                      <TpTooltip
                        content={isDeleting
                          ? t('chat.evidence.deleting')
                          : t('chat.evidence.deleteFromManualFolder', {
                              caseName: getCaseDisplayName(panel.caseId),
                            })}
                        placement="left"
                        delayDuration={100}
                      >
                        <Button
                          type="button"
                          iconOnly
                          size="sm"
                          style="text"
                          color="danger"
                          background={buttonBackground}
                          aria-label={t('chat.evidence.deleteFile', { fileName })}
                          disabled={isDeleting}
                          className="h-7 w-7 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void panel.handlePurgeAdHocManualFile(fileName);
                          }}
                        >
                          <TrashIcon weight="regular" />
                        </Button>
                      </TpTooltip>
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {panel.loadingEvidence || panel.loadingScopes ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-pulse pt-3 font-bold">{t('chat.evidence.loadingSources')}</div>
            </div>
          ) : null}
          {/* No "no results" message here: when search criteria are active
              the tree is mounted above and renders its own empty state. */}
        </>
      )}
    </>
  );

  return (
    <EvidenceLockerAppearanceContext.Provider value={appearance}>
    <>
      {panel.contentModerationAck !== null && (
        <ContentModerationAckModal
          contentModerationType={panel.contentModerationAck.type}
          onCancel={() => panel.setContentModerationAck(null)}
          onAcknowledge={() => {
            const ack = panel.contentModerationAck!;
            panel.setContentModerationAck(null);
            if (ack.ufdr) {
              panel.handleOpenUfdrPreview(ack.ufdr);
              return;
            }
            if (ack.index >= 0) {
              const item = panel.availableEvidence[ack.index];
              panel.handleSetPreviewPopoverOpen(
                ack.index,
                item?.pageCount ?? ack.pageCount ?? undefined,
              );
            } else {
              // Deeply nested file / subdocument not in the flat list — reopen
              // by URL and carry the CM type + obfuscated preview URL so the
              // overlay still shows the CM-safe view by default.
              panel.handleSetPreviewPopoverOpenForUrl(
                ack.url,
                ack.pageCount ?? undefined,
                ack.type,
                ack.previewUrl,
                ack.fileInfo,
              );
            }
          }}
        />
      )}
      {typeof document !== 'undefined' &&
        panel.isSourceCardPopoverOpen &&
        createPortal(
          <DismissibleBlurOverlay
            position="fixed"
            zIndexClass={previewBackdropZIndexClass}
            className="bg-black/50"
            onClose={closePreview}
            closeLabel={t('chat.evidence.closePreview')}
            dismissOnBackdropClick
            showCloseButton={!activePreview}
          />,
          document.body,
        )}
      {activePreview && (
        <EvidenceLockerPreviewOverlay
          isOpen={panel.isSourceCardPopoverOpen}
          url={activePreview.url}
          pageCount={activePreview.pageCount}
          contentModerationType={activePreview.contentModerationType}
          previewUrl={activePreview.previewUrl}
          label={activePreview.label}
          citationSource={activePreview.citationSource}
          showObfuscatedPreview={panel.showObfuscatedPreview}
          onToggleObfuscatedPreview={panel.setShowObfuscatedPreview}
          caseId={panel.caseId}
          previewRef={panel.previewPopoverRef}
          onClose={closePreview}
          handleDownload={panel.handleDownloadWithSession}
          handleAddSourceToScope={(source) => {
            closePreview();
            panel.addSourceToScope(pathForAssistantScope(source));
          }}
          handleCopySourceForTimeline={panel.handleCopySourceForTimeline}
          handleCopyToMediaShare={
            panel.mediaSourceShare?.addToMediaShare
              ? (source, _index, onSuccess?: () => void) =>
                  panel.mediaSourceShare!.addToMediaShare!(source, onSuccess)
              : undefined
          }
          copiedSource={panel.copiedSource}
          addedSource={panel.addedSource}
          addToTimeline={panel.addToTimeline}
          zIndexClass={previewZIndexClass}
        />
      )}
      <EvidenceLockerMobileForensicOverlay
        isOpen={!!panel.ufdrPreviewContext}
        context={panel.ufdrPreviewContext}
        onClose={panel.handleCloseUfdrPreview}
      />
      <div
        className={`flex min-h-0 min-w-0 w-full flex-1 flex-col gap-0${
          inverse ? ' evidence-locker-inverse' : ''
        }`}
        onPointerDown={stopPanelEventBubble}
        onMouseDown={stopPanelEventBubble}
        onClick={stopPanelEventBubble}
      >
        {panel.contentCategoriesEnabled ? (
          <>
            {renderSearchToolbar(
              t('chat.evidence.searchPlaceholder'),
              t('chat.evidence.searchLabel'),
            )}
            {renderSectionDivider()}
            {renderResultsArea('flex min-h-0 flex-1 flex-col gap-2')}
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {renderSearchToolbar(
              t('chat.evidence.searchPlaceholder'),
              t('chat.evidence.searchLabel'),
            )}
            {renderSectionDivider()}
            {renderResultsArea('mt-3 flex min-h-0 flex-1 flex-col gap-2')}
          </div>
        )}
      </div>
    </>
    </EvidenceLockerAppearanceContext.Provider>
  );
}
